import * as React from "react";
import {
  createStyles,
  Paper,
  Theme,
  Fab,
  makeStyles,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
} from "@material-ui/core";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
} from "@material-ui/icons";
import LoaderRow from "./components/LoaderRow";
import { AnvokError } from "lib";
import Create from "./douanes/Create";
import useReactRouter from "use-react-router";
import { useErrorHandler } from "../ErrorHandler";
import DialogDelete from "./components/DialogDelete";
import { useApiClient } from "lib/store";
import { ApiDouaneType, ApiListDouane, ApiDouaneTypeLabel } from "lib/api";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
      overflowX: "auto",
    },
    table: {
      minWidth: 700,
    },
    fab: {
      position: "fixed",
      bottom: theme.spacing(2),
      right: theme.spacing(2),
    },
  })
);

type DeleteData = {
  id: string;
  label: string;
};

const DouaneList: React.FunctionComponent = () => {
  const api = useApiClient();
  const [loading, setLoading] = React.useState(true);
  const [inventories, setInventories] = React.useState<ApiListDouane[]>([]);
  const [create, setCreate] = React.useState(false);
  const [deleteData, setDeleteData] = React.useState<DeleteData>();
  const { history } = useReactRouter();
  const classes = useStyles();
  const errorHandler = useErrorHandler();

  const loadData = React.useCallback(() => {
    setLoading(true);
    api
      .GET("/api/douanes")
      .then((r) => {
        if (r.error) {
          throw new AnvokError(r.error.message);
        }
        setInventories(r.data.items);
      })
      .catch(errorHandler)
      .finally(() => setLoading(false));
  }, [api, errorHandler]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  function handleClose(id?: string, type?: ApiDouaneType) {
    if (id) {
      history.push(
        `/${type === ApiDouaneType.DRM ? "declaration" : "douane"}/${id}`
      );
    } else {
      setCreate(false);
    }
  }

  const handleDelete = React.useCallback(() => {
    if (deleteData) {
      api
        .DELETE("/api/douanes/{id}", {
          params: { path: { id: deleteData.id } },
        })
        .then(() => {
          loadData();
        })
        .catch(errorHandler)
        .finally(() => {
          setDeleteData(undefined);
        });
    }
  }, [api, deleteData, errorHandler, loadData]);

  return (
    <Paper className={classes.root} elevation={1}>
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell>Date création</TableCell>
            <TableCell>Libellé</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>&nbsp;</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <LoaderRow
            show={loading}
            message="Chargement des données..."
            cols={5}
          />
          {inventories.map((inv) => (
            <TableRow key={inv._id}>
              <TableCell>
                {new Date(inv.created).toLocaleDateString()}
              </TableCell>
              <TableCell>{inv.label}</TableCell>
              <TableCell>{ApiDouaneTypeLabel.get(inv.type)}</TableCell>
              <TableCell align="right">
                {!inv.next && (
                  <IconButton
                    color="secondary"
                    onClick={() =>
                      setDeleteData({ id: inv._id, label: inv.label })
                    }
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
                <IconButton
                  onClick={() =>
                    history.push(
                      `/${
                        inv.type === ApiDouaneType.DRM
                          ? "declaration"
                          : "douane"
                      }/${inv._id}`
                    )
                  }
                >
                  {inv.next ? <ViewIcon /> : <EditIcon />}
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Fab
        color="secondary"
        className={classes.fab}
        onClick={() => setCreate(true)}
      >
        <AddIcon />
      </Fab>
      {create && <Create onClose={handleClose} />}
      {deleteData && (
        <DialogDelete
          onCancel={() => setDeleteData(undefined)}
          onConfirm={handleDelete}
        >
          {deleteData.label}
        </DialogDelete>
      )}
    </Paper>
  );
};

export default DouaneList;
